import { TasksOfUserLookup } from "./TasksOfUserLookup";
import { ScheduledTask } from "@/data/models/Task";
import {
  createTimeSlotForSchedueldTask,
  getDayId,
  TimeSlotWithScheduledTask,
} from "./TimeSlot";
import { UserLookup } from "@/data/models/UserLookup";
import { SchedulingSettings } from "@/data/models/SchedulingSettings";

import { OccupiedTimeInfo } from "@/data/models/OccupiedTimeInfo";

export class ScheduledTasksLookup {
  tasksOfUsers = new Map<string, TasksOfUserLookup>();
  private scheduledTasksLookup = new Map<string, ScheduledTask>();

  getOccupiedTimeInfo(startTimestamp: number, endTimestamp: number) {
    const result: OccupiedTimeInfo[] = [];

    for (const [, taskOfUser] of this.tasksOfUsers) {
      result.push(
        ...taskOfUser.getOccupiedTimeInfo(startTimestamp, endTimestamp)
      );
    }

    return result;
  }

  clear() {
    for (const tasksOfUser of this.tasksOfUsers.values()) {
      tasksOfUser.clear();
    }

    this.scheduledTasksLookup.clear();
    this.tasksOfUsers.clear();
  }

  getScheduledTask(taskId: string) {
    return this.scheduledTasksLookup.get(taskId);
  }

  removeScheduledTaskWithId(taskId: string) {
    const scheduledTask = this.scheduledTasksLookup.get(taskId);

    if (!scheduledTask) {
      return;
    }

    this.removeScheduledTask(scheduledTask);
  }

  removeScheduledTask(scheduledTask: ScheduledTask) {
    this.scheduledTasksLookup.delete(scheduledTask.id);

    this.tasksOfUsers
      .get(scheduledTask.userId)
      ?.removeTimeSlotForScheduledTask(scheduledTask, true);
  }

  has(taskId: string) {
    return this.scheduledTasksLookup.has(taskId);
  }

  get allTimeSlotsWithScheduledTasks() {
    const result: TimeSlotWithScheduledTask[] = [];

    for (const [, taskOfUser] of this.tasksOfUsers) {
      result.push(...taskOfUser.allTimeSlotsWithScheduledTasks);
    }

    return result;
  }

  get allScheduledTasks() {
    return this.allTimeSlotsWithScheduledTasks.map((t) => t.scheduledTask);
  }

  getTasksOfUser(userId: string) {
    return this.tasksOfUsers.get(userId);
  }

  getAllTimeSlotsForSameUserAndDay(scheduledTask: ScheduledTask) {
    const tasksOfUser = this.tasksOfUsers.get(scheduledTask.userId);

    if (!tasksOfUser) {
      return [];
    }

    return tasksOfUser.getTasksOnDay(getDayId(scheduledTask.startDate));
  }

  getTasksOfUserLookup(
    userId: string,
    userLookup: UserLookup,
    schedulingSettings: SchedulingSettings
  ) {
    const lookup = this.tasksOfUsers.get(userId);

    if (lookup) {
      return lookup;
    }

    const user = userLookup.getUser(userId);

    if (user) {
      const {
        travelTimeSettings: defaultTravelTimes,
        workingHoursSettings,
        planningControlSettings: calculationSettings,
      } = schedulingSettings;

      const newLookup = new TasksOfUserLookup(
        user,
        [],
        defaultTravelTimes,
        calculationSettings,
        workingHoursSettings
      );

      this.tasksOfUsers.set(userId, newLookup);

      return newLookup;
    }

    return null;
  }

  setScheduledTask(
    scheduledTask: ScheduledTask,
    userLookup: UserLookup,
    schedulingSettings: SchedulingSettings
  ) {
    const previousState = this.scheduledTasksLookup.get(scheduledTask.id);

    this.updateScheduledTask(
      previousState,
      scheduledTask,
      userLookup,
      schedulingSettings
    );
  }

  updateScheduledTask(
    previousState: ScheduledTask | undefined,
    newState: ScheduledTask,
    userLookup: UserLookup,
    schedulingSettings: SchedulingSettings
  ) {
    if (previousState) {
      const previousDayId = getDayId(previousState.startDate);
      const newDayId = getDayId(newState.startDate);

      const previousUserId = previousState.userId;
      const newUserId = newState.userId;

      const hasSlotChanged =
        previousUserId !== newUserId || previousDayId !== newDayId;

      if (hasSlotChanged) {
        this.tasksOfUsers
          .get(previousUserId)
          ?.removeTimeSlotForScheduledTask(previousState, true);
      }
    }

    this.getTasksOfUserLookup(
      newState.userId,
      userLookup,
      schedulingSettings
    )?.addOrUpdateTimeSlotWithSchedueldTask(
      createTimeSlotForSchedueldTask(newState),
      true
    );

    this.scheduledTasksLookup.set(newState.id, newState);
  }

  fillTasksOfUserLookup(
    scheduledTasks: ScheduledTask[],
    userLookup: UserLookup,
    schedulingSettings: SchedulingSettings
  ) {
    this.clear();
    const toFill = this.tasksOfUsers;

    const {
      travelTimeSettings: defaultTravelTimes,
      workingHoursSettings,
      planningControlSettings: calculationSettings,
    } = schedulingSettings;

    scheduledTasks.forEach((task) => {
      this.scheduledTasksLookup.set(task.id, task);

      const { userId } = task;
      if (!toFill.has(userId)) {
        const user = userLookup.getUser(userId);

        if (user) {
          toFill.set(
            task.userId,
            new TasksOfUserLookup(
              user,
              [],
              defaultTravelTimes,
              calculationSettings,
              workingHoursSettings
            )
          );
        }
      }

      toFill
        .get(userId)
        ?.addOrUpdateTimeSlotWithSchedueldTask(
          createTimeSlotForSchedueldTask(task)
        );
    });

    for (const [, taskOfUser] of this.tasksOfUsers) {
      taskOfUser.fillTravelTimeAndPauseDurations();
    }
  }
}
